import React from 'react';
import { Stack, Box, Text, Heading, Link } from '@chakra-ui/react';
import CollapsibleItems from '../../components/shared/CollapsibleItems';
import { SECURITY_EMAIL, PRIVACY_EMAIL } from '../../constants';

const FAQ_ITEMS = [
  {
    title: 'How do I report security or unethical behavior concerns?',
    content: (
      <Text>
        We monitor security activity on our pre-production and production
        systems. Incidents are managed using our Security Incident Management
        process. This process defines the assessment, containment, recovery, and
        remediation of issue resolution. All communication (internal and
        external) is managed by the Critical Response Team. HOVER users,
        Customers, Partners or other outside parties should report security
        issues or unethical behavior to{' '}
        <Link href={`mailto:${SECURITY_EMAIL}`}>{SECURITY_EMAIL}</Link>.
      </Text>
    ),
  },
  {
    title:
      'Who controls which individuals can access the data in my HOVER account?',
    content: (
      <Stack spacing="3">
        <Text>
          If you dwell within a property, you are the account administrator of
          the corresponding HOVER data. You have control over who has access to
          your account.
        </Text>

        <Text>
          If you are part of a business organization with multiple users, each
          creating HOVER models on behalf of your customer residents, you will
          have an assigned HOVER account administrator. The account
          administrator has control over which individuals have access to the
          account. New users invited by the account administrator will follow an
          account sign-up process that includes setting up a password.
        </Text>
      </Stack>
    ),
  },
  {
    title: 'How do you protect my data?',
    content:
      'We protect all data in transit and at rest using standard Transport Layer Security (TLS 1.2 or higher) and the Advanced Encryption Standard (AES-128) protocols. We maintain redundant backups with live-updated standby database systems to maximize system availability.',
  },
  {
    title: 'How do you maintain the quality and security of the HOVER product?',
    content: (
      <Stack spacing="3">
        <Text>
          We use agile software development methodologies to constantly enhance
          your experience with HOVER. Our software engineering teams practice
          continuous delivery to provide quality software to our customers,
          potentially with multiple releases per day. Our dedicated Quality
          Assurance (QA) team ensures the effectiveness of our automated test
          suites, and supplements automation with manual QA when needed.
        </Text>

        <Text>
          We use secure cloud infrastructure to deliver our service to you. Our
          intrusion detection, continuous penetration tests, and vulnerability
          management systems make sure you can use our services safely.
        </Text>
      </Stack>
    ),
  },
  {
    title: 'What are your data retention and deletion policies?',
    content: (
      <Stack spacing="3">
        <Text>
          We do not delete your personal information unless you tell us to, and
          in some cases we must retain data to verify requests. We know that the
          HOVER data is a resource to you, the resident or future resident of
          your property. If you wish to have us remove your personal data from
          our systems and disable your accounts, you may send such a request to{' '}
          <Link href={`mailto:${PRIVACY_EMAIL}`}>{PRIVACY_EMAIL}</Link>.
        </Text>

        <Text>
          We also maintain confidential system and security related logs for a
          minimum of three years. These logs help us monitor system performance
          and serve as a forensic record in the unlikely event of a security
          incident.
        </Text>
      </Stack>
    ),
  },
  {
    title: 'How do you ensure the security of HOVER’s business partners?',
    content:
      'We conduct comprehensive security assessments and annual review on the critical vendors that help us deliver our service. We review service agreements and closely inspect conditions that may lead to misuse of critically important information. We hold our vendors accountable just as we are accountable to you.',
  },
  {
    title: 'Is HOVER SOC2 certified?',
    content:
      'HOVER is SOC2 Type I compliant. HOVER’s current SOC2 report is available to customers and prospects under Non Disclosure Agreement (NDA). Coordinate with your HOVER account representative to request a copy of our SOC2 report.',
  },
];

const FAQSection = () => (
  <Box>
    <Heading mt="14" mb="7">
      FAQ
    </Heading>
    <Text as="p">
      Here’s more information about our privacy and security practices. If you
      don’t find what you are looking for, please check our help center or
      contact us at{' '}
      <Link href={`mailto:${PRIVACY_EMAIL}`}>{PRIVACY_EMAIL}</Link>.
    </Text>
    <CollapsibleItems items={FAQ_ITEMS} />
  </Box>
);

export default FAQSection;
